/**
 * usePageEditorHandlers — Extracted large handlers from PageEditorPage.tsx.
 * Only the two largest handlers are extracted here to keep the interface manageable.
 * (Plan 150, Epic 03)
 */

import { useCallback } from 'react'
import { classifyChange, findSectionByEditorId } from '@zaviago/storefront-config'
import type { AppearanceValue, SectionEnvelope, SectionLookup } from '@zaviago/storefront-config'
import type { EditorSection } from './usePageSections'
import type { SelectedBlockInfo } from './useBlockSelection'
import {
  isAppearancePropKey,
  sectionHasResponsiveOverrides,
  buildFullAppearance,
} from './page-editor-helpers'

interface UsePageEditorHandlersParams {
  selectedSectionId: string | null
  sectionsRef: React.MutableRefObject<EditorSection[]>
  updateSection: (editorId: string, patch: Partial<SectionEnvelope>) => void
  sendCosmeticPatch: (payload: {
    sectionIndex: number
    sectionPath: string
    sectionType: string
    prop: string
    value: unknown
  }) => void
  sendAppearancePatch: (payload: {
    sectionIndex: number
    sectionPath: string
    appearance: AppearanceValue
  }) => void
  cosmeticSkipCountRef: React.MutableRefObject<number>
  findByEditorId: (editorId: string) => SectionLookup<EditorSection> | null
  sections: EditorSection[]
  selectBlock: (info: SelectedBlockInfo) => void
}

/**
 * Returns the two largest handlers extracted from PageEditorPage:
 * - handleSectionUpdate (92 lines — cosmetic classification callback)
 * - handleSelectFirstBlock (17 lines — block selection from section list)
 */
export function usePageEditorHandlers({
  selectedSectionId,
  sectionsRef,
  updateSection,
  sendCosmeticPatch,
  sendAppearancePatch,
  cosmeticSkipCountRef,
  findByEditorId,
  sections,
  selectBlock,
}: UsePageEditorHandlersParams) {
  // Config panel update handler — classifies cosmetic vs structural
  // Branch order matters: responsive appearance → desktop-with-responsive → single cosmetic → structural
  const handleSectionUpdate = useCallback(
    (patch: Parameters<typeof updateSection>[1]) => {
      if (!selectedSectionId) return

      const lookup = findSectionByEditorId(sectionsRef.current, selectedSectionId)
      const section = lookup?.section as EditorSection | undefined
      if (!section) return

      const sectionPath = lookup!.path.join('.')
      const sectionIndex = lookup!.path[0]
      const propKeys = Object.keys(patch.props ?? {})

      // --- Branch 1: Responsive appearance-only patch ---
      const patchKeys = Object.keys(patch)
      const hasOnlyAppearance = patchKeys.length === 1 && patchKeys[0] === 'appearance'
        && (patch as { appearance?: { responsive?: unknown } }).appearance?.responsive !== undefined

      if (hasOnlyAppearance) {
        cosmeticSkipCountRef.current = 1
        updateSection(selectedSectionId, patch)
        const fullAppearance = buildFullAppearance(section, patch)
        sendAppearancePatch({ sectionIndex, sectionPath, appearance: fullAppearance })
        return
      }

      // --- Branch 2: Desktop cosmetic prop WITH existing responsive overrides ---
      const hasResponsiveOverrides = sectionHasResponsiveOverrides(section)
      if (
        propKeys.length === 1 &&
        isAppearancePropKey(propKeys[0]) &&
        hasResponsiveOverrides
      ) {
        cosmeticSkipCountRef.current = 1
        updateSection(selectedSectionId, patch)
        const fullAppearance = buildFullAppearance(section, { props: patch.props })
        sendAppearancePatch({ sectionIndex, sectionPath, appearance: fullAppearance })
        return
      }

      // --- Branch 3: Normal single cosmetic prop (no responsive) ---
      if (
        propKeys.length === 1 &&
        classifyChange(section.type, propKeys[0]) === 'cosmetic'
      ) {
        cosmeticSkipCountRef.current = 1
        updateSection(selectedSectionId, patch)
        sendCosmeticPatch({
          sectionIndex,
          sectionPath,
          sectionType: section.type,
          prop: propKeys[0],
          value: (patch.props!)[propKeys[0]],
        })
        return
      }

      // --- Branch 3b: Slot-only change (no props) — always cosmetic ---
      const slotKeys = Object.keys(patch.slots ?? {})
      if (propKeys.length === 0 && slotKeys.length > 0) {
        cosmeticSkipCountRef.current = 1
        updateSection(selectedSectionId, patch)
        for (const slotKey of slotKeys) {
          const slotVal = (patch.slots!)[slotKey]
          const plainText = typeof slotVal === 'string'
            ? slotVal
            : slotVal && typeof slotVal === 'object' && 'value' in slotVal
              ? (slotVal as { value?: string }).value ?? ''
              : ''
          sendCosmeticPatch({
            sectionIndex,
            sectionPath,
            sectionType: section.type,
            prop: slotKey,
            value: plainText,
          })
        }
        return
      }

      // --- Branch 4: Structural path (full CONFIG_UPDATE) ---
      cosmeticSkipCountRef.current = 0
      updateSection(selectedSectionId, patch)
    },
    [selectedSectionId, updateSection, sendCosmeticPatch, sendAppearancePatch],
  )

  // Select the first block in a block-enabled section (context menu action)
  const handleSelectFirstBlock = useCallback((editorId: string) => {
    const lookup = findByEditorId(editorId)
    if (!lookup) return
    const section = lookup.section as EditorSection
    if (!section.blocks?.length) return
    const firstBlock = section.blocks[0]
    const sectionPath = findSectionByEditorId(sections, editorId)?.path.join('.') ?? '0'
    selectBlock({
      sectionPath,
      blockId: firstBlock.id,
      blockType: firstBlock.type,
      content: firstBlock.content,
      widthPct: firstBlock.layout.widthPct,
    })
  }, [sections, findByEditorId, selectBlock])

  return { handleSectionUpdate, handleSelectFirstBlock }
}
